import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { InstallAppButton } from "@/components/InstallAppButton";
import { LoginOtpStep } from "@/components/LoginOtpStep";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(null);
  const done = () => { toast.success("Connexion réussie"); nav("/"); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email.trim(), password);
      if (r?.otp_required) setOtp(r); else done();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between blueprint-bg p-12 relative overflow-hidden">
        <div className="flex items-center gap-3 relative z-10">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white shrink-0">
            <img src="/logo-omnibat.png" alt="OMNIBAT ONE" className="h-12 w-12 object-contain" />
          </div>
          <p className="font-head text-2xl font-extrabold text-white tracking-tight">OMNIBAT<span className="text-emerald-400"> ONE</span></p>
        </div>
        <div className="relative z-10">
          <h1 className="font-head text-4xl xl:text-5xl font-extrabold text-white leading-tight tracking-tight">
            Le système interne<br />de pilotage d'OMNIBAT
          </h1>
          <p className="text-slate-300 mt-5 max-w-md leading-relaxed">
            Commercial · Organisation · Collaborateurs · Projets internes · Temps · Rapports. Une plateforme unique, des données consolidées pour la Direction.
          </p>
          <div className="flex items-center gap-2 mt-8 text-emerald-300 text-sm font-mono">
            <ShieldCheck className="h-4 w-4" /> Accès sécurisé par rôle
          </div>
        </div>
        <p className="text-xs text-slate-500 relative z-10 font-mono">© 2026 OMNIBAT — BTP & Ingénierie</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <img src="/logo-omnibat.png" alt="OMNIBAT ONE" className="h-11 w-11 object-contain" />
            <p className="font-head text-xl font-extrabold tracking-tight">OMNIBAT ONE</p>
          </div>
          <h2 className="font-head text-2xl font-bold text-slate-900">Bienvenue</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">{otp ? "Vérification de votre identité" : "Connectez-vous à votre espace"}</p>

          {otp ? <LoginOtpStep challenge={otp} onDone={done} onBack={() => { setOtp(null); setPassword(""); }} /> : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Adresse email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" placeholder="vous@omnibat.com" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  required data-testid="login-password" placeholder="••••••••" className="h-11 pr-11" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} data-testid="toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label={showPwd ? "Masquer" : "Afficher"}>
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end -mt-2"><Link to="/mot-de-passe-oublie" className="text-sm text-brand-700 hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</Link></div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-brand-600 hover:bg-brand-700 text-white font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>
          )}

          <div className="mt-8">
            <div className="flex justify-center"><InstallAppButton variant="login" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}
